import { useState, useEffect } from 'react'
import { Key, AlertTriangle, Copy, RefreshCw, Loader2, XCircle, CheckCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useProxyConfig } from '@/hooks/useProxyConfig'

interface BackupStatus {
  remaining: number
  lowWarning: boolean
}

export function ProviderBackupCodesManager() {
  const { proxyUrl, proxyToken } = useProxyConfig()
  const headers: Record<string, string> = proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}

  const [status, setStatus] = useState<BackupStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [confirmRegen, setConfirmRegen] = useState(false)
  const [newCodes, setNewCodes] = useState<string[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { loadStatus() }, [proxyUrl])

  async function loadStatus() {
    if (!proxyUrl) return
    setLoading(true)
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/backup-codes/status`, { headers })
      const data = await r.json() as BackupStatus
      setStatus(data)
    } catch {
      setError('Error al cargar estado de backup codes')
    } finally {
      setLoading(false)
    }
  }

  async function handleRegenerate() {
    if (!proxyUrl) return
    setGenerating(true); setError(null); setConfirmRegen(false)
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/backup-codes/generate`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      })
      const data = await r.json() as { backupCodes: string[] }
      setNewCodes(data.backupCodes)
      await loadStatus()
    } catch {
      setError('Error al regenerar códigos')
    } finally {
      setGenerating(false)
    }
  }

  function copyAll(codes: string[]) {
    navigator.clipboard.writeText(codes.join('\n'))
      .then(() => toast.success('Códigos copiados al portapapeles'))
  }

  return (
    <div className="flex flex-col gap-3">
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <XCircle className="w-3.5 h-3.5" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-white/30" />
        </div>
      ) : status && (
        <>
          {/* Status badge */}
          {status.remaining === 0 ? (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/25 rounded-xl px-3 py-2.5">
              <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 text-xs font-medium">Sin códigos de respaldo</p>
                <p className="text-white/35 text-[10px] mt-0.5">Genera nuevos códigos para tener acceso de emergencia.</p>
              </div>
            </div>
          ) : status.lowWarning ? (
            <div className="flex items-start gap-2 bg-amber-500/8 border border-amber-500/20 rounded-xl px-3 py-2.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-400 text-xs font-medium">Solo te quedan {status.remaining} código{status.remaining !== 1 ? 's' : ''}</p>
                <p className="text-white/35 text-[10px] mt-0.5">Regenera pronto para no quedarte sin acceso de emergencia.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 bg-white/5 border border-white/8 rounded-xl px-3 py-2.5">
              <Key className="w-3.5 h-3.5 text-white/40 shrink-0" />
              <p className="text-white/60 text-xs">{status.remaining} código{status.remaining !== 1 ? 's' : ''} de respaldo disponible{status.remaining !== 1 ? 's' : ''}</p>
            </div>
          )}

          {/* New codes display (after regeneration) */}
          {newCodes && (
            <div className="bg-black/20 border border-white/10 rounded-xl p-4 flex flex-col gap-3">
              <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-medium">
                <CheckCircle className="w-3.5 h-3.5" />
                Nuevos códigos generados
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {newCodes.map((c, i) => (
                  <div key={i} className="font-mono text-xs text-white/80 bg-white/5 border border-white/8 rounded-lg px-2.5 py-1.5 text-center tracking-wider">
                    {c}
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-1.5 text-amber-400/70 text-[10px]">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                No se mostrarán de nuevo. Guárdalos en un lugar seguro.
              </div>
              <button onClick={() => copyAll(newCodes)} className="flex items-center justify-center gap-1.5 py-2 rounded-lg text-white/60 text-xs font-medium border border-white/10 hover:bg-white/8 hover:text-white transition-all">
                <Copy className="w-3 h-3" />
                Copiar todos
              </button>
              <button onClick={() => setNewCodes(null)} className="text-center text-[10px] text-white/30 hover:text-white/60 transition-colors">
                Cerrar
              </button>
            </div>
          )}

          {/* Regenerate */}
          {!confirmRegen ? (
            <button
              onClick={() => setConfirmRegen(true)}
              disabled={generating}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-white/60 text-sm font-medium border border-white/10 hover:bg-white/8 hover:text-white transition-all disabled:opacity-40"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Regenerar backup codes
            </button>
          ) : (
            <div className="bg-red-500/8 border border-red-500/20 rounded-xl p-4 flex flex-col gap-3">
              <p className="text-white/70 text-xs leading-relaxed">
                Esto <strong className="text-white">invalida todos los códigos anteriores</strong>. ¿Estás seguro?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmRegen(false)}
                  className="flex-1 border border-white/12 text-white/50 hover:text-white text-xs py-2 rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={generating}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-red-500/15 border border-red-500/30 hover:bg-red-500/25 text-red-400 text-xs font-semibold py-2 rounded-xl transition-all disabled:opacity-40"
                >
                  {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Sí, regenerar
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
